import { useCallback, useRef } from 'react';
import { Folder, Upload } from 'lucide-react';
import { useStore } from '../store.js';
import { isTauri, pickFolder, readRemedy, startWatching } from '../tauri.js';

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: {
      mode?: 'read' | 'readwrite';
    }) => Promise<FileSystemDirectoryHandle>;
  }
}

async function readRemedyFromDirectory(
  handle: FileSystemDirectoryHandle,
): Promise<{ source: string; fileHandle: FileSystemFileHandle } | null> {
  // entries() is defined on FileSystemDirectoryHandle at runtime in browsers
  // that support the File System Access API; lib.dom types don't expose it.
  const iter = (
    handle as unknown as {
      entries: () => AsyncIterableIterator<[string, FileSystemHandle]>;
    }
  ).entries();
  for await (const [name, child] of iter) {
    if (name === '.remedy' && child.kind === 'file') {
      const fileHandle = child as FileSystemFileHandle;
      const file = await fileHandle.getFile();
      const source = await file.text();
      return { source, fileHandle };
    }
  }
  return null;
}

export function DashboardView() {
  const setSource = useStore((s) => s.setSource);
  const fileInput = useRef<HTMLInputElement>(null);

  const openFolder = useCallback(async () => {
    // Desktop (Tauri) path: native folder picker → read via Rust host.
    if (isTauri()) {
      const folder = await pickFolder();
      if (!folder) return;
      try {
        const file = await readRemedy(folder);
        setSource(file.source, `${folder}/.remedy`, { name: folder });
        await startWatching(folder);
      } catch (e) {
        alert(`Could not open ${folder}: ${(e as Error).message}`);
      }
      return;
    }

    // Web path: File System Access API.
    if (!window.showDirectoryPicker) {
      alert(
        'Your browser does not support the File System Access API. Use "Upload .remedy" instead, or try Chrome/Edge.',
      );
      return;
    }
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      const result = await readRemedyFromDirectory(dirHandle);
      if (!result) {
        alert(`No .remedy file found in ${dirHandle.name}.`);
        return;
      }
      setSource(result.source, `${dirHandle.name}/.remedy`, {
        name: dirHandle.name,
        dirHandle,
        fileHandle: result.fileHandle,
      });
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        console.error('openFolder failed', e);
      }
    }
  }, [setSource]);

  const onUpload = useCallback(
    (file: File) => {
      file.text().then((source) => {
        setSource(source, file.name, { name: file.name });
      });
    },
    [setSource],
  );

  const fsSupported = isTauri() || (typeof window !== 'undefined' && !!window.showDirectoryPicker);

  return (
    <div className="flex h-full flex-col overflow-auto p-8">
      <header className="mb-8">
        <h1 className="text-[22px] font-semibold text-ink-100">Open a Remedy</h1>
        <p className="mt-1 text-[13px] text-ink-400">
          Every Remedy is a folder with a <code className="mono">.remedy</code> file at its root. Pick a folder from disk, or drop a single file in if the browser doesn't support folder access.
        </p>
      </header>

      <div className="grid max-w-3xl grid-cols-1 gap-3 md:grid-cols-2">
        <button
          onClick={openFolder}
          disabled={!fsSupported}
          className="group flex flex-col items-start gap-3 rounded-md border border-ink-800 bg-ink-900 p-5 text-left transition-colors hover:border-accent-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <div className="rounded bg-ink-800 p-2 text-accent-500 group-hover:bg-ink-700">
            <Folder size={20} strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-ink-100">Open folder…</div>
            <div className="mt-1 text-[12px] text-ink-400">
              {fsSupported
                ? 'Pick a Remedy directory from disk. Live-reloads when the .remedy file changes.'
                : 'File System Access API not available in this browser. Try Chrome or Edge.'}
            </div>
          </div>
        </button>

        <button
          onClick={() => fileInput.current?.click()}
          className="group flex flex-col items-start gap-3 rounded-md border border-ink-800 bg-ink-900 p-5 text-left transition-colors hover:border-accent-500"
        >
          <div className="rounded bg-ink-800 p-2 text-accent-500 group-hover:bg-ink-700">
            <Upload size={20} strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-ink-100">Upload a .remedy file</div>
            <div className="mt-1 text-[12px] text-ink-400">
              Works everywhere. Changes you make stay in-browser; use "Save…" to export.
            </div>
          </div>
          <input
            type="file"
            ref={fileInput}
            className="hidden"
            accept=".remedy,.yaml,.yml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
        </button>
      </div>

      <hr className="my-8 border-ink-800" />

      <h2 className="label">What this editor does</h2>
      <ul className="mt-2 space-y-1.5 text-[12px] text-ink-400">
        <li>• Parses and validates against the Remedy JSON Schema, in-browser.</li>
        <li>• Canonicalises per SPEC §2.1 (key order, indent, folded scalars).</li>
        <li>• Shows features, flows, lint warnings live as you edit.</li>
        <li>• Builds embedded artifacts against OpenAI if you paste an API key into Build.</li>
      </ul>
    </div>
  );
}
